// API Response / Request related types
// Note: They should begin with R
// R meaning Response

import type { JsonSchema } from '@shared/types'
import type { StatusDisplay } from 'v2/pages/10dlc/types'
import type { AgentState } from 'v2/pages/flow/types'
import type { PlanType } from 'v2/types'
import type { UserRole } from './auth'

// Users
interface RUserTeam {
  aom_id: string
  created_at: string
  id: string
  role_name: string
  team_name: string
  team_uuid: string
  updated_at_epoch: number
}
export interface RUserSkills {
  skill_level?: number
  uuid: string
}
interface RUserRole {
  id: string
  name: string
}
export interface RUser {
  aom_uuid: string
  account_name: string
  email: string
  skills: RUserSkills[]
  roles: RUserRole[]
  status: string
  teams: RUserTeam[]
  is_human_agent: boolean
  is_seat_occupied: boolean
  default_call_number?: string | null
}

export interface RUsersList {
  members_list: RUser[]
  total_count: number
}
export interface RUserDelete {
  email: string
  transfer_numbers_to_email: string
}
export interface RAllowedBuyCountries {
  allowed_countries: string[]
}
export interface RUserUpdate {
  roles?: string[]
  team_uuids: string[]
  skills: RUserSkills[]
  is_human_agent: boolean
  default_call_number?: string
}

// Timezone
export interface RTimezone {
  id: string
  name: string
  short_form: string
  tz_value: string
}

// Destinations
export interface RDestination {
  uuid?: string
  name: string
  destination_identifier: string
  destination_type: string
}

export interface RDestinationsList {
  destinations: RDestination[]
  total: number
}
export interface RTimezonesList {
  timezone_list: RTimezone[]
}

export interface RBusinessHours {
  id: string
  name: string
  description: string
  timezone: RTimezone
  default: boolean
  associated_workflow_count: number
  uuid?: string
}
interface RAssociatedWorkflow {
  enabled: boolean
  name: string
  workflow_id: string
}
export interface RHolidays {
  date: string
  id: string
  name: string
  selected: boolean
  status: 'open' | 'close'
  time_slots:
    | {
        end_time: string
        start_time: string
        time_slot_id: string
      }[]
    | null
}
export interface RBusinessHoursPayload {
  id: string
  name: string
  description: string
  timezone: RTimezone
  associated_workflows: RAssociatedWorkflow[]
  schedule: {
    day: string
    time_slots: {
      start_time: string
      end_time: string
      time_slot_id: string
    }[]
  }[]
  holiday_group_id: string
  holidays: RHolidays[]
}

export interface RBusinessHoursList {
  business_hours: RBusinessHours[]
  total_count: number
}

// Holidays
export interface RHolidayGroup {
  count_holidays: number
  country: string
  id: string
  name: string
}
export interface RHolidayGroupsList {
  groups: RHolidayGroup[]
  total_count: number
}
export interface RHolidayDetails {
  country: string
  holidays: {
    date: string
    id: string
    name: string
    status: string
    time_slots:
      | {
          end_time: string
          start_time: string
          time_slot_id: string
        }[]
      | null
  }[]
  id: string
  name: string
}
export interface RHolidayCreateResponse {
  date: string
  id: string
  name: string
  status: string
}
// Teams
export interface RMemebers {
  member_uuid: string
}
export interface RTeam {
  created_at: string
  id: string
  members_count: number
  members_list: RMemebers[]
  name: string
  updated_at_epoch: number
}

export interface RTeamsList {
  organization: string
  teams: RTeam[]
  total_count: number
}

export interface RRole {
  id: string
  name: string
  is_predefined: boolean
  parent_role: string | null
  created_at?: string
  updated_at?: number
}

export interface RRolesList {
  organization: string
  roles: RRole[]
}

export interface RTeamCreatePayload {
  name: string
  member_ids: string[]
}

export interface RAgentStatusUpdate {
  reason_for_change: string
  status_uuid: string
}

export interface RTeamUpdatePayload {
  name: string
  add_member_ids: string[]
  remove_member_ids: string[]
}
// Skills
export interface RSkill {
  created_at: string
  id: string
  is_level_required: boolean
  max_level: number | null
  min_level: number | null
  name: string
  updated_at_epoch: number
}

export interface RDisposition {
  created_at?: string
  disabled?: boolean
  id?: string
  uuid?: string
  name?: string
  sub_dispositions?: RDisposition[]
  updated_at?: string
  type?: string
  reorder_sub_dispositions?: boolean
  newlyAdded?: boolean
}

export interface RDispositionReorderChild {
  parentItem: RDisposition
  reorderedData: RDisposition[]
  data?: RDisposition[]
}

export interface RDispositionStatus {
  checked: boolean
  parentItem: RDisposition
  data: RDisposition[]
  type: string
}

export interface RSkillsList {
  skills_list: RSkill[]
  total_count: number
}

export interface RDispositionList {
  dispositions: RDisposition[]
  total_count: number
}

export interface RDispositionUpdate {
  data: RDisposition[]
  disposition: RDisposition
  type: string
}

// Status
export interface RStatusList {
  max_limit_reached: boolean
  org_statuses: ROrgStatus[]
  status_colors: string[]
}

export interface ROrgStatus {
  availability: boolean
  color: string
  default: boolean
  intermediate: boolean
  organisation: number
  sequence: number
  status: string
  uuid: string
}

interface EventMetadata {
  inactivity?: {
    value: number
    unit: string
    validations: {
      max_value: number
      min_value: number
    }
  }
  enable_tracking?: {
    value: boolean
    unit: string
  }
  allowed_status_availability?: boolean | null
}

interface StatusConfiguration {
  value: string
  event_metadata?: EventMetadata
}

export interface RStatusConfigurations {
  sign_in_status: StatusConfiguration
  sign_out_status: StatusConfiguration
  at_capacity_status: StatusConfiguration
  idle_status: StatusConfiguration
}

// Phone numbers

export enum ComplianceApplicationStatus {
  DRAFT = 'draft',
  SUBMITTED = 'submitted',
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
  // Terminal states the reviewer/renewal side can set. India CAFs carry an
  // expiry and a renewal nudge runs against it, so these do reach the customer.
  EXPIRED = 'expired',
  SUSPENDED = 'suspended',
}

export enum ComplianceStatus {
  IN_REVIEW = 'in-review',
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
}

interface RPhoneNumberTeam {
  id: string
  name: string
}
interface RPhlos {
  channel_name: string
  phlo_name: string
  phlo_uuid: string
}
export interface RPhoneNumber {
  association_type: string
  call_forwarding_enabled: boolean
  city: string
  country: string
  countries: Array<{ country: string; country_code: string }>
  created_at: string
  default_caller_id: boolean
  external: boolean
  id: string
  is_sender_id: boolean
  is_verified: boolean
  mms_enabled: boolean
  name_tag: string
  is_selected?: boolean
  number: string
  number_type: string
  outgoing_enabled: boolean
  phlo_ids: Array<{
    channel_name: string
    phlo_name: string
    phlo_uuid: string
    secondary_flow_data: {
      is_enabled: boolean
      percent: number
      phlo_name: string
      phlo_uuid: string
    } | null
    // Embed sub-account sessions only: another tenant's agent, sent with uuid and name nulled.
    hidden?: boolean
  }>
  region: string
  sms_enabled: boolean
  source: string
  status: string
  teams: RPhoneNumberTeam[]
  updated_at_epoch: number
  voice_enabled: boolean
  whatsapp_enabled: boolean
  phone_number_id?: string
  business_account_name?: string
  compliance_status?: ComplianceStatus
  compliance_application_id?: string
  tendlc_campaign_id?: string | null
  // 10DLC campaign registration status (US-only); status_display carries the badge label+variant
  tendlc_registration_status?: string | null
  tendlc_status_display?: StatusDisplay
  waba_linked?: boolean
  name_status?: string
  quality_rating?: string
  whatsapp_account_id?: string
  application_url?: string
  mps_allowed?: string
  subaccount_auth_id?: string
  log_incoming_request?: string
  wa_callback_url?: string
  wa_answer_url?: string
  is_internal: boolean
  // High Availability Number fields
  ha_enabled?: boolean
  fallback_number?: string
  // App configuration fields
  app_type?: 'agent' | 'xml' | 'trunk'
  app_id?: string
  trunk_id?: number
  sub_account?: string
  sub_account_name?: string
  // CNAM (US-only): per-number Caller-ID NAME + CNAM Lookup toggle
  cnam_lookup_enabled?: boolean
  cnam?: string
}

// Embed number attach/detach. Attach sends the agent id (replace moves the number off another
// agent); detach sends 'none' plus the agent it expects to remove the number from.
export interface RAgentNumberPayload {
  call_workflow_id: string
  replace?: boolean
  from_workflow_id?: string
}

// `errors` of a 409 from the embed attach/detach route.
export interface RAgentNumberConflict {
  code: 'number_in_use' | 'number_changed' | 'number_has_split' | 'number_has_app'
  current_agent: { id: string; name: string } | null
}

// Response for the per-number CNAM bulk endpoints. The backend may report per-number
// failures; the shape is defensive so partial-failure handling degrades gracefully to a
// generic error toast when the field is absent.
export interface RNumberCnamFailure {
  number: string
  error?: string
  message?: string
}

export interface RNumberCnamUpdate {
  message?: string
  // Per-number failures, when present. `failed`/`errors` are accepted aliases.
  failed?: RNumberCnamFailure[]
  errors?: RNumberCnamFailure[]
}

// Bulk unrent (release) of purchased numbers. The core endpoint loops the numbers
// server-side and reports exactly which could not be released (reusing the CNAM
// failure shape: { number, error? }).
export interface RNumberBulkDelete {
  total?: number
  deleted?: number
  // Per-number failures. `failed`/`errors` are accepted aliases (mirrors RNumberCnamUpdate).
  failed?: RNumberCnamFailure[]
  errors?: RNumberCnamFailure[]
}

// Available numbers
export interface RNumberSearch {
  city: string
  country: string
  mms_enabled: boolean
  monthly_rental_rate: string
  number_type: string
  phone_number: string
  region: string
  restriction: string
  restriction_text: string | null
  search_param: string
  setup_rate: string
  sms_enabled: boolean
  voice_enabled: boolean
}

// Flows
export interface RFlows {
  applications: {
    id: string
    name: string
    type: string
  }[]
  auth_id: string
  /** Subaccount this agent is tagged to (SA…), or null/absent when untagged. */
  sub_auth_id?: string | null
  created_at: number
  knowledge_base_ids: string[]
  description: string
  enabled: boolean
  group: string
  id: string
  phlo_type: string
  is_default: boolean
  name: string
  numbers: Array<{
    alias: string
    channel: string
    number: string
  }>
  public: boolean
  updated_at: number
  url: string
  version: number
  updated_by: string
  state: AgentState
  is_legacy?: boolean
  /** False only for migrated legacy flows (still owned by the old console). Duplicates of legacy flows are legacy but editable. */
  is_editable?: boolean
  /** Root legacy flow uuid this flow descends from (self for migrated flows). */
  legacy_flow_id?: string
  /** Immediate source flow uuid when this is a duplicate of a legacy flow. */
  duplicated_from?: string
  created_by: {
    uuid: string
    name: string
    email: string
  }
}

export interface RDuplicateFlow {
  message: string
  phlo_id: string
  redact?: boolean
  success: boolean
  /** True when the source agent was tagged to a subaccount. */
  source_was_tagged?: boolean
  /** True when that tag carried over to the copy. False means the subaccount is no
   * longer usable (disabled or gone), so the copy starts on the main account. */
  tag_copied?: boolean
}

/** Slim flow entry from GET /contacto-config/phlo/options (SER-5833) — only what
 * dropdowns render/filter on. Anything heavier (numbers, applications, creator)
 * still requires the full RFlows list. */
export interface RFlowOption {
  id: string
  name: string
  phlo_type: string
  state: AgentState
  enabled: boolean
  is_default: boolean
  is_legacy?: boolean
  is_editable?: boolean
  legacy_flow_id?: string | null
  duplicated_from?: string | null
}

/** Always-paginated envelope of /phlo/options. Unlike GET /phlo, this path skips
 * core's response filter, so `total` survives to the client. */
export interface RFlowOptionsPayload {
  objects: RFlowOption[]
  total: number
  limit: number
  page: number
  paginated: boolean
}

// Queues
interface ConfigHoldMusic {
  file: string
  prompt_type: string
  url: string
}
interface ConfigEventsUpdate {
  isUpdated: boolean
  method: string
  url: string
}
// Base interface for common fields
interface BaseChannelConfigMessages {
  conversation_closed_message: string
  post_agent_assignment_message: string
  pre_agent_assignment_message: string
}

// Specific interfaces for each configuration type
interface ChatConfigMessages extends BaseChannelConfigMessages {
  queue_chat_timeout: number
}

interface WhatsappConfigMessages extends BaseChannelConfigMessages {
  queue_whatsapp_timeout: number
}

interface MessageConfigMessages extends BaseChannelConfigMessages {
  queue_message_timeout: number
}
interface ConfigPrompt {
  file: string
  language: string
  loop_with_others: boolean
  loops: number
  prompt_name: string
  prompt_speak_type: string
  prompt_type: string
  text: string
  url: string
  voice: string
  delay?: number | undefined
}
export interface RQueueConfig {
  agent_hold_music: ConfigHoldMusic
  callback_config: { queue_callback_timeout: number }
  channel: string[]
  chat_config: ChatConfigMessages
  customer_hold_music: ConfigHoldMusic
  events: {
    after_conversation_work_completed: ConfigEventsUpdate
    agent_assigned: ConfigEventsUpdate
    conversation_transferred: ConfigEventsUpdate
  }
  message_config: MessageConfigMessages
  prompt: ConfigPrompt[]
  dequeue_order: string
  queue: string
  queue_call_allowed_inputs: string[]
  queue_call_timeout: number
  queue_capacity: number
  queue_priority: number
  task_config: { queue_task_timeout: number }
  whatsapp_config: WhatsappConfigMessages
}
export interface RQueue {
  attributes: string
  config: RQueueConfig
  enabled: boolean
  name: string
  queue_phlo: RPhlos[]
  uuid: string
}

// Teams
export interface RNonTeamMember {
  email: string
  id: string
  name: string
  roles: UserRole[]
}

export interface RNonTeamMembersList {
  non_team_members: RNonTeamMember[]
  total_count: number
}

// Verified Caller IDs
export interface VerifiedCallerIdFields {
  number: string
  alias_name: string
  verification_channel: VerificationChannel
  subaccount?: string
}

/**
 * Sandbox verified destinations (India voice trial).
 *
 * A trial account may only place outbound calls to OTP-verified numbers.
 * number_service returns verified and pending rows as separate arrays and
 * reports the enforced cap on every list response - always read `cap` from
 * the API rather than hardcoding it, it has already changed once.
 */
export enum SandboxOtpChannel {
  SMS = 'sms',
  VOICE = 'voice',
  /** number_service accepts this today: `oneof=sms voice whatsapp`. */
  WHATSAPP = 'whatsapp',
}

export interface RSandboxNumber {
  phone_number: string
  country_iso?: string
  verified: boolean
  verified_at?: string | null
  verification_channel?: SandboxOtpChannel
  verification_sent_at?: string | null
  created_at?: string
}

/**
 * The account's signup / 2FA number, surfaced read-only on the list response.
 * It is already callable in sandbox but cannot be added, verified, resent or
 * deleted through these endpoints, and it counts toward neither cap.
 */
export interface RSandboxSignupNumber {
  phone_number: string
  verified: boolean
  verification_method?: string
}

export interface RSandboxNumbers {
  verified: RSandboxNumber[]
  pending: RSandboxNumber[]
  signup_number?: RSandboxSignupNumber | null
  /** Max verified entries. */
  cap: number
  /** Max rows overall, verified + pending. Sits above `cap` so a user at the
   *  verified limit still has room for in-flight attempts. */
  total_cap: number
}

export interface RSandboxAddPayload {
  phone_number: string
  country_iso: string
  channel: SandboxOtpChannel
}

/** 202 on dispatch; 200 with no session_uuid when the number was already verified. */
export interface RSandboxAddResponse {
  phone_number: string
  channel?: SandboxOtpChannel
  session_uuid?: string
  message?: string
}

export interface RSandboxVerifyResponse {
  phone_number: string
  verified: boolean
  message?: string
}

export interface RVerifiedCallerId {
  phone_number: string
  alias: string
  country: string
  verification_uuid: string
  created_at: string
  modified_at: string
  resource_uri?: string
  subaccount?: string
}

export interface RVerifiedCallerIds {
  objects: RVerifiedCallerId[]
  meta: {
    limit: number
    next: string | null
    offset: number
    previous: string | null
    total_count: number
  }
}

// Paginated Phone Numbers Response
export interface RPhoneNumbersPaginated {
  numbers: RPhoneNumber[]
  total: number
}

// Edit Number
export interface RconfigureNumber {
  alias_name: string
  team_uuids: [string]
  call_workflow_id: string
  msg_workflow_id: string
  whatsapp_workflow_id: string
  outgoing_enabled: boolean
}
export enum VerificationChannel {
  SMS = 'sms',
  CALL = 'call',
}

// Profile Permissions
interface Permission {
  supervisor?: boolean
  user?: boolean
  admin?: boolean
  // Added new permission profiles
  manager?: boolean
  specialist?: boolean
  finance_analyst?: boolean
  developer?: boolean
  support?: boolean
}

interface ViewPermission {
  supervisor?: string
  user?: string
  // Added new permission profiles
  manager?: string
  specialist?: string
  finance_analyst?: string
  developer?: string
  support?: string
}

export interface RProfilePermissions {
  conv_calls_and_recordings: {
    download_call_recording: Permission
    download_conversation: Permission
    download_conversation_list: Permission
    view: ViewPermission
  }
  knowledge_base_article: {
    create: Permission
    delete: Permission
    edit: Permission
    publish: Permission
  }
  knowledge_base_category: {
    create: Permission
    delete: Permission
    edit: Permission
  }
  monitor_all_channels: boolean
  monitor_and_barge_conv: {
    bargein_conversation: Permission
    monitor_conversation: Permission
  }
}

// Routing Settings
interface RCapacity {
  call: number
  callback: number
  chat: number
  email: number
  message: number
  task: number
  whatsapp: number
}

interface RExpiry {
  expiry_time_unit: string
  expiry_time_value: number
  no_expiry: boolean
}

interface ROutboundStickyRouting {
  enabled: boolean
  expiry: RExpiry
}

interface RReservationTimeout {
  call: number
  callback: number
  chat: number
  email: number
  message: number
  task: number
  whatsapp: number
}

interface RWrapUpConfiguration {
  unit: string
  wrap_up_time: number
}

export interface RRoutingSettings {
  auto_accept: boolean
  capacity: RCapacity
  outbound_sticky_routing: ROutboundStickyRouting
  release_capacity_on_resolve: boolean
  reservation_timeout: RReservationTimeout
  wrap_up_configuration: RWrapUpConfiguration
}
interface RTimeout {
  timeout_in_seconds: number
  ui_timeout_unit: string
}
interface RConversationTimeout {
  call: RTimeout
  callback: RTimeout
  chat: RTimeout
  email: RTimeout
  message: RTimeout
  task: RTimeout
  whatsapp: RTimeout
}

export interface RConversationSettings {
  wrap_up_configuration: RWrapUpConfiguration // v1/contacto-core/routing_settings
  conversation_timeout: RConversationTimeout // v1/contacto-core/orgs/settings
  conversation_page_row_count: number // v1/contacto-core/settings/aom
  conversation_permissions: {
    download_call_recording: Permission
    download_conversation: Permission
    download_conversation_list: Permission
    view: ViewPermission
  } // v1/contacto-core/rbac/update_profile_permission
  transfer_settings: {
    allow_agent_transfer_type: boolean
    default_transfer_type: string
    transfer_disposition: string
    transfer_sub_disposition: string
  }
}

export interface RAgentSettings {
  conversation_page_row_count: number
  default_call_number?: string | null
}

// Channel configurations
export interface RChannelConfiguration {
  default_caller_id: string
  outbound_phlo: string
  channel_settings?: {
    allow_noise_reduction: boolean
    allow_personal_caller_ids: boolean
    allow_recording_pause_play: boolean
    call_recording_inbound: boolean
    call_recording_outbound: boolean
  }
}

// User profile

interface RPubnubKeysetData {
  publish_key: string
  subscribe_key: string
}

interface RSipEndpoint {
  password: string
  username: string
}

export interface ROrganization {
  allow_noise_reduction: boolean
  allow_personal_caller_ids: boolean
  allow_recording_announcement: boolean
  allow_recording_pause_play: boolean
  allow_user_recording_override: boolean
  always_collect_feedback: boolean
  call_recording_inbound: boolean
  call_recording_outbound: boolean
  calling_component: string
  conference_addition: string
  default_caller_id: string
  email_channel_support: boolean
  id: string
  max_numbers: number
  min_spend_commit: boolean
  name: string
  org_auth_id: string
  org_call_recording: boolean
  org_conference_addition: string
  org_created_at: string
  // When the Plivo account itself was created (account-service). Distinct from
  // org_created_at (when the CX org was provisioned) — null/absent when
  // account-service doesn't return it, so never fall back to org_created_at.
  plivo_account_created_at?: string | null
  org_id: string
  org_show_personal_numbers: boolean
  pubnub_keyset_data: RPubnubKeysetData
  push_contact_details_async_flag: boolean
  release_capacity_on_resolve: boolean
  reorder_contact_tabs: boolean
  roles: string[]
  sip_endpoint: RSipEndpoint
  skip_account_setup: boolean
  onboarding_completed?: boolean
  skip_case_creation: boolean
  skip_contact_creation: boolean
  status: string
  timezone: string
  timezone_title: string
  web_app_support: boolean
  wrap_up_time: string
  org_plivo_account_id: string
  subscribed_seats: number
  active_seats: number
  cps_allowed?: number
  inbound_cps_allowed?: number
  mps_allowed?: number
  payment_period?: number
  plan?: PlanType
  // Plivo product plan, distinct from `plan` above (Contacto tier). Core always
  // sends both keys. Name is free-form, so it can't map to a fixed label set.
  product_plan_id: number | null
  product_plan_name: string | null
  is_human_agent: boolean
  is_internal_support_user: boolean
  is_seat_occupied: boolean
  traffic_destination: string
  human_specialist_enabled?: boolean
  rps: number
  concurrent_calls: number
  tendlc_submission_enabled?: boolean
  tollfree_submission_enabled?: boolean
  // Disabled-style account flags: only an explicit true gates the module,
  // absent/false keeps it visible (SER-5789).
  verify_disabled?: boolean
  // Org owner's email, shown read-only in Organization settings. Optional:
  // backend adds this separately — safe to render as absent until deployed.
  owner_email?: string
  whatsapp_disabled?: boolean
}

export interface RSipCredentials {
  sip_endpoint: {
    username: string
    password: string
  }
}

export interface RAccountConcurrency {
  concurrency: number
  outbound_cps: number
  // Caps per dial prefix ("91160"); only procured series, absent when none.
  series_concurrency?: Record<string, number>
}

export interface RUsageLimit {
  limit_value: number
  maximum_limit?: number
  excluded?: boolean
  exclusion_reason?: string
}

export interface UsageLimitPayload {
  limit_value: number
  maximum_limit?: number
  excluded?: boolean
  exclusion_reason?: string
}

interface SipEndpoint {
  password: string
  username: string
}

export interface RUserProfile {
  account_first_name: string
  account_last_name: string
  account_name: string
  active_session_count: number
  created_at: string
  email: string
  id: string
  last_login: string
  mpc_max_members: number
  only_admin: boolean
  organizations: ROrganization[]
  phlo_base_url: string
  plan?: PlanType
  sip_endpoint: SipEndpoint
}

interface RTimezonePayload {
  tzinfo: string
  title: string
}

export interface RUserProfilePayload {
  name: string
  bio?: string
  timezone: RTimezonePayload
}

// Additional email recipients (account-scoped "key contacts")
export interface RKeyContacts {
  emails: string[]
}

export interface RKeyContactsPayload {
  emails: string[]
}

// Internal Numebers
export interface RInternalNumber {
  category: string
  contact_id: number
  contact_type: string
  custom_1_datetime: string
  custom_1_url: string
  custom_bool: boolean
  custom_date: string
  custom_number: string
  email: string
  first_name: string
  first_picklist: string
  home_phone: string
  language: string
  last_name: string
  multiple_picklist: string
  other_phone: string
  phone_number: string
  phone_number_extension: string
  timezone: string
  work_phone: string
}

export interface RWhatsappTemplate {
  ID: number
  auth_id: string
  data: {
    // Meta/WhatsApp template ID (from messaging-whatsapp). Optional: populated
    // after a template sync; older cached rows may not have it yet.
    id?: string
    category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION'
    components: Array<{
      type: string
      format?: string
      text?: string
      add_security_recommendation?: boolean
      code_expiration_minutes?: number
      buttons?: Array<{
        type: string
        text: string
        url?: string
        example?: string[]
        phone_number?: string
        otp_type?: 'COPY_CODE' | 'ONE_TAP' | 'ZERO_TAP'
        autofill_text?: string
        supported_apps?: Array<{ package_name: string; signature_hash: string }>
        zero_tap_terms_accepted?: boolean
        package_name?: string
        signature_hash?: string
      }>
    }>
    language: string
    name: string
    namespace: string
    rejected_reason: string
    status: 'approved' | 'pending' | 'rejected' | 'draft'
  }
  uuid: string
  waba_id: string
}

export interface RWhatsappTemplatesResponse {
  sender_ids: string[]
  templates_data: {
    count: number
    waba_templates: RWhatsappTemplate[]
  }
  whatsapp_account_id: string
}

export interface WhatsAppAccount {
  is_plivo: boolean
  sender_ids: string[]
  whatsapp_account_id: string
  whatsapp_account_name: string
  application_url?: string
  mps_allowed?: string
  subaccount_auth_id?: string
  log_incoming_request?: string
}

// Add this new interface at the end of the file
export interface RWhatsAppMediaUploadResponse {
  media_handler: string
}

export interface UpdateTemplatePayload {
  data: CreateTemplatePayload
}

export interface CreateTemplatePayload {
  name: string
  category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION'
  status: 'draft' | 'submitted'
  language: string
  components: Array<
    | {
        type: 'HEADER'
        format: 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'NONE'
        text?: string
        example?: { header_text: string[] } | { header_handle: string[] }
      }
    | {
        type: 'BODY'
        text?: string
        example?: { body_text: string[][] }
        add_security_recommendation?: boolean
      }
    | {
        type: 'FOOTER'
        text?: string
        code_expiration_minutes?: number
      }
    | {
        type: 'BUTTONS'
        buttons: Array<{
          text: string
          type: 'QUICK_REPLY' | 'CALL_TO_ACTION' | 'OTP'
          uuid?: number
          url?: string
          example?: string[]
          phone_number?: string
          otp_type?: 'COPY_CODE' | 'ONE_TAP' | 'ZERO_TAP'
          autofill_text?: string
          supported_apps?: Array<{ package_name: string; signature_hash: string }>
          zero_tap_terms_accepted?: boolean
          package_name?: string
          signature_hash?: string
        }>
      }
  >
}

export enum OAuthType {
  API_KEY = 'api_key',
}

export interface RExternalAppDetails {
  custom_fields_enabled: boolean
  /** What the connector does. Optional, so readers fall back to the auth type. */
  description?: string
  /** 1-based rank among the most-connected apps; null or absent when not popular. */
  popular_rank?: number | null
  id: string
  input_schema: JsonSchema
  logo_url: string
  name: string
  priority: number
  oauth_type: OAuthType
  provider: string
}

export interface RExternalApp {
  CRM: RExternalAppDetails[]
  Communications: RExternalAppDetails[]
  Marketplace: RExternalAppDetails[]
  Other: RExternalAppDetails[]
}

export interface RActionDetails {
  action_name: string
  description: string
  input_schema: JsonSchema
  output_schema: JsonSchema
}

export enum AppCategory {
  ECOMMERCE = 'ecommerce',
  HELPDESK = 'helpdesk',
  CRM = 'crm',
}

export interface RExternalOrgAppConnection {
  connection: Record<string, unknown>
  credentials: Record<string, unknown>
}

export interface RExternalOrgApp {
  actions: RActionDetails[]
  connection_details: RExternalOrgAppConnection | null
  is_custom_fields_enabled: boolean
  app_category: AppCategory
  id: string
  name: string
  provider: string
  state: string
  priority: number
  logo_url: string
  created_on: string
  integrated_by: {
    id: string
    name: string
    email: string
  }
  custom_field_entity: string[]
  account_identifier: Record<string, unknown>
  users_with_access: {
    id: string
    name: string
    email: string
  }[]
  shared_with_org?: boolean
}

export interface ROAuthProvider {
  app_provider: string
  name: string
  logo_url: string
  auth_type: string
  configurable: boolean
  configured: boolean
  client_id?: string
  callback_url?: string
}

export interface RAciBridgeConnectResponse {
  owner_id: string
  consent_url?: string
  oauth_redirect_url?: string
  linked_account?: Record<string, unknown>
}

export interface RAciBridgeAccessRecords {
  owner?: string
  org?: boolean
  users: string[]
}

export interface RCustomMcpServer {
  id: string
  name: string
  slug: string
  url: string
  auth_type: string
  /** Non-secret auth metadata; null on the modes that don't use them. The credential is never returned. */
  header_name?: string | null
  username?: string | null
  state: string
  tools: { name: string; description?: string; input_schema?: unknown }[]
}

export interface OrgAppConnectionPayload {
  app_uuid: string
  connection_details: RExternalOrgAppConnection | null
  enabled: boolean
  priority_order: number
}

export interface ROrgAppConnection {
  org_app: {
    id: string
    name: string
    provider: string
    priority: number
    connection_details: RExternalOrgAppConnection
    state: string
  }
  oauth_redirect_url?: string
  api_id?: string
}

export interface RExportRecordingParams {
  viewUuid: string
  conversationId: string
  chunkId: string
}

export interface RTranscription {
  speaker: string
  start_time: number
  text: string
}

export interface ROrganizationLite {
  org_uuid: string
  org_name: string
  region: string
}

export interface RSwitchOrganizationPayloadV2 {
  org_uuid: string
  region: string
}

export interface RInitiateCallerIdVerification {
  api_id: string
  message: string
  verification_uuid: string
}

export interface RVerifyCallerId {
  alias: string
  api_id: string
  channel: string
  country: string
  created_at: string
  phone_number: string
  verification_uuid: string
}

export interface InitiateCallerIdVerificationPayload {
  phone_number: string
  alias?: string
  channel?: VerificationChannel
  subaccount?: string
}

export interface VerifyCallerIdPayload {
  otp: string
}

export interface RSubaccount {
  auth_id: string
  name: string
  enabled: boolean
  auth_token?: string
  new_auth_token?: string
  auth_token_expire_time?: string
  created?: string
  modified?: string | null
  account?: string
  account_id: number
  phone_numbers_count?: number
  resource_uri?: string
}

export interface RSubaccounts {
  objects: RSubaccount[]
  meta: {
    limit: number
    next: string | null
    offset: number
    previous: string | null
    total_count: number
  }
}

// IP Access Control List Types
export interface IPAccessControlList {
  ipacl_uuid: string
  name: string
  ip_addresses: string[]
}

export interface IPAccessControlListPayload {
  name: string
  ip_addresses: string[]
}

export interface RIPAccessControlList {
  meta: {
    limit: number
    offset: number
    total_count: number
    previous: string | null
    next: string | null
  }
  objects: IPAccessControlList[]
}

export interface RCreateIPAccessMutation {
  message: string
  ipacl_uuid: string
}

// Credential Types
export interface Credential {
  credential_uuid: string
  name: string
  username: string
  password?: string
}

export interface CredentialPayload {
  name?: string
  username?: string
  password?: string
}

export interface RCredential {
  meta: {
    limit: number
    offset: number
    total_count: number
    previous: string | null
    next: string | null
  }
  objects: Credential[]
}
export interface RCredentialMutation {
  credential_uuid: string
}

// Origination URI Types
export interface OriginationURIPayload {
  name: string
  uri: string
  authentication_needed: boolean
  username?: string
  password?: string
}

export interface ROriginationURI {
  meta: {
    limit: number
    offset: number
    total_count: number
    previous: string | null
    next: string | null
  }
  objects: Array<{
    uri_uuid: string
    name: string
    uri: string
    authentication_needed: boolean
    username?: string
    password?: string
  }>
}

export interface ROriginationURIMutation {
  message: string
  uri_uuid: string
}

/**
 * Single-record shape of GET /v1/Account/{auth_id}/Zentrunk/URI/{uri_uuid}/
 * (the public API passthrough, verified live 2026-09-04). FLAT -- unlike
 * ROriginationURI (the paginated list shape, `{ meta, objects }`), a single
 * record has no wrapper. This is the only origination-URI response that
 * carries sip_user: the internal core proxy's list endpoint (get_uris) does
 * not return it at all, confirmed against a real OpenAI trunk where sip_user
 * was correctly on the wire for every call while the list read back empty.
 */
export interface RPublicOriginationURI {
  api_id: string
  uri_uuid: string
  name: string
  uri: string
  authentication_needed: boolean
  username?: string
  sip_user?: string
}

// Internal API Origination URI Response (from Core API)
export interface ROriginationURIInternal {
  id: number
  is_auth: boolean
  name: string
  timeout: number
  uri: string
  uuid: string
  username?: string
  /** Fixed SIP user for the INVITE Request-URI (zentrunk sip_user); '' when unset */
  sip_user?: string
}

export interface ROriginationURIsInternal {
  api_id: string
  data: ROriginationURIInternal[]
  errors: null | Record<string, unknown>
  message: string
}

export interface RSipTrunkPlatform {
  trunk_ref: string
  trunk_direction: 'inbound' | 'outbound'
  platform: string
}

/**
 * trunk_ref -> platform, for every trunk the org has recorded one for. A map
 * rather than a list because the console resolves a whole page of trunks against
 * it by id. Absent entries are legacy trunks created before we recorded this.
 */
export interface RSipTrunkPlatforms {
  platforms: Record<string, { platform: string; trunk_direction: 'inbound' | 'outbound' }>
}

export interface RPlivoApplication {
  answer_method: string
  application_type?: string
  answer_url: string
  app_id: string
  app_name: string
  default_app: boolean
  enabled: boolean
  fallback_answer_url: string
  fallback_method: string
  hangup_method: string
  hangup_url: string
  message_method: string
  message_url: string
  public_uri: boolean
  resource_uri: string
  sip_uri: string
  sub_account: string | null
  log_incoming_messages: boolean
}

export interface RPlivoApplicationInternal extends RPlivoApplication {
  account_id: number
  application_type: string
  created_at: string | null
  direct_dial: boolean
  endpoints: string[]
  endpoints_count: number
  id: number
  phone_numbers: string[]
  phone_numbers_count: number
  production_app: boolean
  sub_account_id: number | null
  updated_at: string | null
}

export interface RPlivoApplications {
  objects: RPlivoApplication[]
  meta: {
    limit: number
    next: string | null
    offset: number
    previous: string | null
    total_count: number
  }
}

export interface RPlivoApplicationsInternal {
  objects: RPlivoApplicationInternal[]
  meta: {
    limit: number
    next: string | null
    offset: number
    previous: string | null
    total_count: number
  }
}

export interface RPlivoNumber {
  number: string
  alias: string | null
  sub_account: string | null
  added_on: string
  application: string | null
  carrier: string
  region: string
  number_type: string
  monthly_rental_rate: string
  renewal_date: string
  sms_enabled: boolean
  sms_rate: string
  voice_enabled: boolean
  voice_rate: string
  resource_uri: string
  tendlc_campaign_Id?: string
  tendlc_registration_status?: string
  toll_free_sms_verification?: string | null
}

export interface RPlivoNumbers {
  api_id: string
  objects: RPlivoNumber[]
  meta: {
    limit: number
    next: string | null
    offset: number
    previous: string | null
    total_count: number
  }
}

export interface ContactViewConfigSource {
  uuid: string
  name: string
  app_provider: string
}

export interface RContactViewConfigResponse {
  id: string
  contact_details_source: ContactViewConfigSource | null
  actions_to_show: string[]
  // Absent from an ACI deployed before caller CRM lookup.
  crm_card_enabled?: boolean
  crm_lookup_apps?: string[]
}

export interface UpsertContactViewConfigPayload {
  contact_details_source_uuid: string | null
  actions_to_show: string[]
}
